/**
 * Service API Sites (US-005)
 * Gestion Multi-Sites
 */

import { api, ApiResponse } from '@e-store/shared';

// Types
export type SiteType = 'siege' | 'magasin';
export type SiteStatus = 'active' | 'inactive';

export interface SiteAddress {
  street: string;
  city: string;
  postalCode: string;
  country: string;
}

export interface SiteContact {
  phone?: string;
  email?: string;
  managerName?: string;
}

export interface Site {
  id: string;
  name: string;
  type: SiteType;
  status: SiteStatus;
  address: SiteAddress;
  contact: SiteContact;
  isDefault: boolean;
}

export interface CreateSiteInput {
  name: string;
  type: SiteType;
  address: SiteAddress;
  contact?: SiteContact;
  isDefault?: boolean;
}

export interface UpdateSiteInput {
  name?: string;
  type?: SiteType;
  status?: SiteStatus;
  address?: SiteAddress;
  contact?: SiteContact;
  isDefault?: boolean;
}

/**
 * Service API Sites
 */
class SitesApiService {
  /**
   * Alias pour getAllSites - compatibilité avec l'ancienne API
   */
  async list(): Promise<{ sites: Site[]; total: number }> {
    const response = await this.getAllSites();
    if (response.data) {
      return { sites: response.data.sites, total: response.data.total };
    }
    throw new Error(response.error || 'Erreur lors du chargement des sites');
  }

  /**
   * Alias pour getAllSites - utilisé par SalesReportPage
   */
  async getSites(): Promise<{ sites: Site[] }> {
    const response = await this.getAllSites();
    if (response.data) {
      return { sites: response.data.sites };
    }
    throw new Error(response.error || 'Erreur lors du chargement des sites');
  }

  /**
   * Récupérer tous les sites avec pagination
   */
  async getAllSites(
    type?: string,
    status?: string,
    search?: string,
    page?: number,
    pageSize?: number
  ): Promise<ApiResponse<{ sites: Site[]; total: number; page: number; pageSize: number; hasMore: boolean }>> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    if (page) params.append('page', String(page));
    if (pageSize) params.append('pageSize', String(pageSize));
    const queryString = params.toString();
    return api.get<{ sites: Site[]; total: number; page: number; pageSize: number; hasMore: boolean }>(
      `/api/sites${queryString ? `?${queryString}` : ''}`
    );
  }

  /**
   * Récupérer les sites actifs
   */
  async getActiveSites(): Promise<ApiResponse<{ sites: Site[] }>> {
    return api.get<{ sites: Site[] }>('/api/sites/active');
  }

  /**
   * Récupérer le site par défaut
   */
  async getDefaultSite(): Promise<ApiResponse<{ site: Site }>> {
    return api.get<{ site: Site }>('/api/sites/default');
  }

  /**
   * Récupérer un site par ID
   */
  async getSiteById(id: string): Promise<ApiResponse<{ site: Site }>> {
    return api.get<{ site: Site }>(`/api/sites/${id}`);
  }

  /**
   * Créer un nouveau site
   */
  async createSite(data: CreateSiteInput): Promise<ApiResponse<{ site: Site; message: string }>> {
    return api.post<{ site: Site; message: string }>('/api/sites', data);
  }

  /**
   * Mettre à jour un site
   */
  async updateSite(id: string, data: UpdateSiteInput): Promise<ApiResponse<{ site: Site; message: string }>> {
    return api.put<{ site: Site; message: string }>(`/api/sites/${id}`, data);
  }

  /**
   * Supprimer un site
   */
  async deleteSite(id: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/api/sites/${id}`);
  }
}

export const sitesApi = new SitesApiService();
